import type {
  MenuItemAssignCategoryRequest,
  MenuCategoryPartnerOptionsSuccess,
  MenuCustomizationGroupPartnerOptionsSuccess,
  MenuItemCustomizationGroupAssignmentDto,
  MenuItemItemOptionsSuccess,
  MenuItemOptionDto,
  MenuItemPartnerCreateOptionsSuccess,
  MenuItemUpdateStatusByItemRequest,
  AssignMenuItemCustomizationGroupsRequest,
  CustomizationGroupStatus,
  MenuCategoryItemOptionDto,
  MenuCustomizationGroupManagementDto,
  MenuItemManagementDto,
  MenuCategoryManagementDto,
  MenuCategoryManagementPage,
  MenuItemStatus,
  MenuListItemDto
} from '~/services/api/generated'
import {
  CustomizationGroupStatusSchema,
  MenuCategoryStatusSchema,
  MenuItemStatusSchema,
  SelectionTypeSchema
} from '~/services/api/generated'
import { z } from 'zod'
const statusNarrowingSchema = <Status extends string, Resource extends { status: unknown }>(
  statusSchema: z.ZodType<Status>
) =>
  z.custom<Resource>().transform((resource): Omit<Resource, 'status'> & { status: Status } => ({
    ...resource,
    status: statusSchema.parse(resource.status)
  }))

export const menuItemOptionStatusNarrowingSchema = statusNarrowingSchema<
  MenuItemStatus,
  MenuItemOptionDto
>(MenuItemStatusSchema)

const menuItemCustomizationGroupStatusNarrowingSchema = statusNarrowingSchema<
  CustomizationGroupStatus,
  MenuItemCustomizationGroupAssignmentDto
>(CustomizationGroupStatusSchema)

export type DashboardCatalogMeta = MenuCategoryManagementPage['pagination']

export type DashboardUpdateItemStatusPayload = MenuItemUpdateStatusByItemRequest

export type DashboardAssignItemCategoryPayload = MenuItemAssignCategoryRequest

export type DashboardAssignItemCustomizationGroupsPayload = AssignMenuItemCustomizationGroupsRequest

// `MenuCategoryFiltersSuccess['data']` is `unknown[]` in the generated contract
// (the OpenAPI operation lost its array-element type), so the element shape is
// validated here at the trust boundary instead of derived from the generated type.
export const dashboardCategoryFilterSchema = z.object({
  id: z.int(),
  name: z.string()
})

export type DashboardCategoryFilter = z.output<typeof dashboardCategoryFilterSchema>

export type DashboardCategoryOptionResource =
  MenuCategoryPartnerOptionsSuccess['data']['items'][number]

type MenuItemManagementDtoWithRequiredFields = MenuItemManagementDto & {
  customization_group_ids: NonNullable<MenuItemManagementDto['customization_group_ids']>
  customization_groups: NonNullable<MenuItemManagementDto['customization_groups']>
  dietary_attribute_ids: NonNullable<MenuItemManagementDto['dietary_attribute_ids']>
  dietary_attributes: NonNullable<MenuItemManagementDto['dietary_attributes']>
}

const hasRequiredItemFields = (
  resource: MenuItemManagementDto
): resource is MenuItemManagementDtoWithRequiredFields =>
  resource.customization_group_ids !== null &&
  resource.customization_groups !== null &&
  resource.dietary_attribute_ids !== null &&
  resource.dietary_attributes !== null

export const DashboardCatalogView = {
  category(resource: MenuCategoryManagementDto) {
    const status = MenuCategoryStatusSchema.parse(resource.status)
    const items = resource.items?.map((item) => menuItemOptionStatusNarrowingSchema.parse(item))

    return {
      activity_metric: resource.activity_metric,
      assigned_items_count: resource.assigned_items_count,
      delivery_items_count: resource.delivery_items_count,
      description: resource.description,
      duplicate_name_count: resource.duplicate_name_count,
      id: resource.id,
      is_assigned: resource.is_assigned,
      menu_ids: resource.menu_ids,
      menus: resource.menus,
      name: resource.name,
      status,
      usage_count: resource.usage_count,
      ...(items === undefined ? {} : { items })
    }
  },

  item(resource: MenuItemManagementDto) {
    if (!hasRequiredItemFields(resource)) {
      throw new TypeError('Item response omits fields required by the dashboard item view.')
    }

    const status = MenuItemStatusSchema.parse(resource.status)
    const customizationGroups = resource.customization_groups.map((group) =>
      menuItemCustomizationGroupStatusNarrowingSchema.parse(group)
    )

    let menuCategory:
      Pick<NonNullable<MenuItemManagementDto['menu_category']>, 'id' | 'name'> | null | undefined
    if (resource.menu_category === null) {
      menuCategory = null
    } else if (resource.menu_category !== undefined) {
      menuCategory = { id: resource.menu_category.id, name: resource.menu_category.name }
    }

    return {
      created_at: resource.created_at,
      customization_group_ids: resource.customization_group_ids,
      customization_groups: customizationGroups,
      default_price: resource.default_price,
      description: resource.description,
      dietary_attribute_ids: resource.dietary_attribute_ids,
      dietary_attributes: resource.dietary_attributes,
      id: resource.id,
      individual_packaging_available: resource.individual_packaging_available,
      is_available: resource.is_available,
      is_featured: resource.is_featured,
      is_package: resource.is_package,
      menu_category_id: resource.menu_category_id,
      menu_ids: resource.menu_context?.menu_ids ?? [],
      minimum_guest_count: resource.minimum_guest_count,
      name: resource.name,
      out_of_stock: resource.out_of_stock,
      placements: resource.placements ?? [],
      photo: resource.photo,
      photo_path: resource.photo_path,
      price: resource.price,
      price_type: resource.price_type,
      serves_count: resource.serves_count,
      sort_order: resource.sort_order,
      status,
      tax_category: resource.tax_category,
      updated_at: resource.updated_at,
      ...(menuCategory === undefined ? {} : { menu_category: menuCategory }),
      ...(resource.menu_context === undefined ? {} : { menu_context: resource.menu_context }),
      ...(resource.store === undefined ? {} : { store: resource.store })
    }
  },

  itemOptions(
    data: MenuItemPartnerCreateOptionsSuccess['data'] | MenuItemItemOptionsSuccess['data']
  ) {
    const category =
      'menu_category_id' in data.defaults ? data.defaults.menu_category_id : data.defaults.category

    return {
      categories: data.categories,
      customization_groups: data.customization_groups,
      defaults: {
        category,
        individual_packaging_available: data.defaults.individual_packaging_available,
        is_featured: data.defaults.is_featured,
        is_package: data.defaults.is_package,
        menu_id: 'menu_id' in data.defaults ? data.defaults.menu_id : null,
        price_type: data.defaults.price_type,
        weight_unit: data.defaults.weight_unit
      },
      dietary_attributes: data.dietary_attributes,
      menus: 'menus' in data ? data.menus : [],
      price_types: data.price_types,
      weight_units: data.weight_units
    }
  },

  menu(resource: MenuListItemDto) {
    return resource
  },

  modifier(
    resource: Omit<
      MenuCustomizationGroupManagementDto,
      'max_per_option' | 'max_total_selections'
    > & { max_per_option: number; max_total_selections: number }
  ) {
    if (resource.options === undefined || resource.options === null) {
      throw new TypeError(
        'Modifier response omits options required by the dashboard modifier view.'
      )
    }

    const status = CustomizationGroupStatusSchema.parse(resource.status)
    const selectionType = SelectionTypeSchema.parse(resource.selection_type)
    return {
      allow_multiple_selections: resource.allow_multiple_selections,
      allow_repeated_selection: resource.allow_repeated_selection,
      description: resource.description,
      id: resource.id,
      is_required: resource.is_required,
      linked_items: resource.linked_items,
      max_per_option: resource.max_per_option,
      max_selections: resource.max_selections,
      max_total_selections: resource.max_total_selections,
      min_selections: resource.min_selections,
      name: resource.name,
      note: resource.note,
      options: resource.options,
      options_count: resource.options_count,
      menus: resource.menus,
      selection_type: selectionType,
      sort_order: resource.sort_order,
      status
    }
  },

  modifierItemOption(resource: MenuCategoryItemOptionDto) {
    const items = resource.items.map((item) => menuItemOptionStatusNarrowingSchema.parse(item))
    return { id: resource.id, items, name: resource.name }
  },

  modifierOption(resource: MenuCustomizationGroupPartnerOptionsSuccess['data']['items'][number]) {
    return { ...resource, options_count: null, status: null }
  }
}

export type DashboardCatalogCategoryResource = ReturnType<typeof DashboardCatalogView.category>
export type DashboardCatalogItemResource = ReturnType<typeof DashboardCatalogView.item>
export type DashboardItemOptionsResource = ReturnType<typeof DashboardCatalogView.itemOptions>
export type DashboardCatalogMenuResource = ReturnType<typeof DashboardCatalogView.menu>
export type DashboardCatalogModifierResource = ReturnType<typeof DashboardCatalogView.modifier>
export type DashboardModifierItemOptionResource = ReturnType<
  typeof DashboardCatalogView.modifierItemOption
>
export type DashboardModifierOptionResource = Omit<
  ReturnType<typeof DashboardCatalogView.modifierOption>,
  'menus'
>
